// frontend/src/pages/BlogPost.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiService, getFullImageUrl } from '../services/api';
import SEOHead from '../components/SEOHead';
import { 
  Calendar, 
  User, 
  Eye, 
  Heart, 
  MessageCircle, 
  Share, 
  Clock, 
  ArrowRight, 
  Bookmark,
  Loader,
  AlertCircle,
  Send,
  ThumbsUp,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [commentForm, setCommentForm] = useState({
    name: '',
    email: '',
    content: '',
    parent: null
  });
  const [replyingTo, setReplyingTo] = useState(null);

  useEffect(() => {
    fetchPostData();
  }, [slug]);

  const fetchPostData = async () => {
    try {
      setLoading(true);
      setError(null);

      // دریافت اطلاعات پست
      const postResponse = await apiService.getPost(slug);
      const posts = postResponse.data.results || postResponse.data;
      
      if (posts.length === 0) {
        setError('مقاله مورد نظر یافت نشد');
        return;
      }

      const postData = posts[0];
      setPost(postData);

      // دریافت نظرات
      await fetchComments(postData.id);

      // دریافت مقالات مرتبط
      await fetchRelatedPosts(postData.id);

    } catch (error) {
      console.error('Error fetching post data:', error);
      setError('خطا در دریافت اطلاعات مقاله');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (postId) => {
    try {
      const response = await apiService.getPostComments(postId);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const fetchRelatedPosts = async (postId) => {
    try {
      const response = await apiService.getRelatedPosts(postId);
      setRelatedPosts(response.data);
    } catch (error) {
      console.error('Error fetching related posts:', error);
      // اگر API مربوطه وجود ندارد، از محصولات مشابه استفاده کن
      const similarResponse = await apiService.getPosts({
        category: post?.category?.slug,
        exclude: postId
      });
      setRelatedPosts(similarResponse.data.results || similarResponse.data.slice(0, 3));
    }
  };

  const handleLike = async () => {
    if (!post) return;

    try {
      await apiService.likePost(post.id);
      setLiked(!liked);
      // در حالت واقعی تعداد لایک‌ها آپدیت می‌شود
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleBookmark = () => {
    setBookmarked(!bookmarked);
  };

  const handleCommentChange = (e) => {
    setCommentForm({
      ...commentForm,
      [e.target.name]: e.target.value
    });
  };

  const handleReply = (comment) => {
    setCommentForm({
      ...commentForm,
      parent: comment.id,
      content: `@${comment.name} `
    });
    setReplyingTo(comment);
    setShowCommentForm(true);
  };

  const cancelReply = () => {
    setCommentForm({
      ...commentForm,
      parent: null,
      content: ''
    });
    setReplyingTo(null);
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!post) return;

    setSubmitting(true);

    try {
      await apiService.submitComment(post.id, commentForm);
      
      // ریست فرم
      setCommentForm({
        name: '',
        email: '',
        content: '',
        parent: null
      });
      setShowCommentForm(false);
      setReplyingTo(null);
      
      // رفرش نظرات
      await fetchComments(post.id);
      
      // نمایش پیام موفقیت
      alert('نظر شما با موفقیت ثبت شد و پس از تایید نمایش داده می‌شود.');
      
    } catch (error) {
      console.error('Error submitting comment:', error);
      alert('خطا در ثبت نظر. لطفاً دوباره تلاش کنید.');
    } finally {
      setSubmitting(false);
    }
  };

  const calculateReadTime = (content) => {
    if (!content) return 5;
    // محاسبه زمان مطالعه بر اساس تعداد کلمات
    const wordsPerMinute = 200;
    const words = content.split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return minutes;
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return 'اخیراً';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'دیروز';
    if (diffDays < 7) return `${diffDays} روز پیش`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} هفته پیش`;
    return `${Math.ceil(diffDays / 30)} ماه پیش`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'تاریخ نامشخص';
    return new Date(dateString).toLocaleDateString('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const sharePost = () => {
    if (navigator.share) {
      navigator.share({
        title: post?.title,
        text: post?.excerpt,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('لینک مقاله کپی شد');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader className="animate-spin h-12 w-12 text-yellow-400 mx-auto mb-4" />
          <p className="text-gray-400">در حال دریافت مقاله...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">خطا در دریافت مقاله</h2>
          <p className="text-gray-400 mb-4">{error}</p>
          <div className="flex space-x-4 space-x-reverse justify-center">
            <button 
              onClick={() => navigate(-1)}
              className="bg-gray-700 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
            >
              بازگشت
            </button>
            <Link 
              to="/blog"
              className="bg-yellow-400 text-gray-900 px-6 py-2 rounded-lg hover:bg-yellow-300 transition-colors"
            >
              برو به بلاگ
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-2xl font-bold text-white mb-2">مقاله یافت نشد</h2>
          <Link to="/blog" className="text-yellow-400 hover:text-yellow-300">
            بازگشت به بلاگ
          </Link>
        </div>
      </div>
    );
  }

  const readTime = calculateReadTime(post.content);
  const articleSchema = {
    title: post.title,
    description: post.excerpt,
    image: getFullImageUrl(post.featured_image),
    publishedDate: post.published_at,
    modifiedDate: post.updated_at,
    author: post.author_name
  };

  return (
    <>
      <SEOHead 
        title={post.title}
        description={post.excerpt}
        keywords={`${post.title}, ${post.category_name}قالب سازی، لاستیک سازی، طراحی و ساخت انواع قالب، تولید قطعات پلاستیکی، `}
        image={getFullImageUrl(post.featured_image)}
        type="article"
        article={articleSchema}
      />
      
      <div className="min-h-screen bg-black py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 space-x-reverse text-sm text-gray-400 mb-8">
            <Link to="/" className="hover:text-white transition-colors">خانه</Link>
            <ArrowRight size={16} />
            <Link to="/blog" className="hover:text-white transition-colors">بلاگ</Link>
            <ArrowRight size={16} />
            <span className="text-yellow-400">{post.category_name}</span>
          </nav>

          {/* Article Header */}
          <article className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden mb-8">
            {/* Featured Image */}
            {post.featured_image && (
              <div className="relative h-80 md:h-96">
                <img
                  src={getFullImageUrl(post.featured_image)}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60"></div>
              </div>
            )}

            {/* Article Meta */}
            <div className="p-6 md:p-8">
              <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-gray-400">
                <div className="flex items-center space-x-1 space-x-reverse">
                  <Calendar size={16} />
                  <span>{formatDate(post.published_at)}</span>
                </div>
                <div className="flex items-center space-x-1 space-x-reverse">
                  <User size={16} />
                  <span>{post.author_name}</span>
                </div>
                <div className="flex items-center space-x-1 space-x-reverse">
                  <Eye size={16} />
                  <span>{post.view_count?.toLocaleString() || '۰'} بازدید</span>
                </div>
                <div className="flex items-center space-x-1 space-x-reverse">
                  <Clock size={16} />
                  <span>زمان مطالعه: {readTime} دقیقه</span>
                </div>
                {post.comment_count > 0 && (
                  <div className="flex items-center space-x-1 space-x-reverse">
                    <MessageCircle size={16} />
                    <span>{post.comment_count} نظر</span>
                  </div>
                )}
              </div>

              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                {post.title}
              </h1>

              <p className="text-xl text-gray-300 mb-6 leading-relaxed">
                {post.excerpt}
              </p>

              {/* Action Buttons */}
              <div className="flex items-center justify-between border-t border-gray-800 pt-6">
                <div className=" items-center space-x-4 space-x-reverse">
                  <button
                    onClick={handleLike}
                    className={`flex items-center space-x-2 space-x-reverse px-4 py-2 rounded-lg transition-colors ${
                      liked 
                        ? 'bg-red-500 text-white' 
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    <Heart fill={liked ? 'currentColor' : 'none'} size={20} />
                    <span>{liked ? 'لایک شده' : 'لایک'}</span>
                  </button>
                    <br />
                  <button
                    onClick={handleBookmark}
                    className={`flex items-center space-x-2 space-x-reverse px-4 py-2 rounded-lg transition-colors ${
                      bookmarked 
                        ? 'bg-yellow-500 text-white' 
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    <Bookmark fill={bookmarked ? 'currentColor' : 'none'} size={20} />
                    <span>{bookmarked ? 'ذخیره شده' : 'ذخیره'}</span>
                  </button>
                </div>
                <div className="flex items-center space-x-2 space-x-reverse">
                  <button 
                    onClick={sharePost}
                    className="p-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <Share size={20} />
                  </button>
                  <button 
                    onClick={() => setShowCommentForm(true)}
                    className="flex items-center space-x-2 space-x-reverse px-4 py-2 bg-yellow-400 text-gray-900 rounded-lg hover:bg-yellow-300 transition-colors"
                  >
                    <MessageCircle size={20} />
                    <span>ثبت نظر</span>
                  </button>
                </div>
              </div>
            </div>
          </article>

          {/* Article Content */}
          <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 md:p-8 mb-8">
            <div 
              className="max-w-4xl w-full max-h-[90vh] overflow-y-auto prose prose-invert prose-lg max-w-none blog-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </div>

          {/* Tags */}
          <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 mb-8">
            <div className="flex flex-wrap gap-2">
              <span className="text-gray-400">برچسب‌ها:</span>
              <span className="bg-gray-800 text-yellow-400 px-3 py-1 rounded-lg text-sm">
                {post.category_name}
              </span>
              <span className="bg-gray-800 text-gray-300 px-3 py-1 rounded-lg text-sm">
                صنعت
              </span>
              <span className="bg-gray-800 text-gray-300 px-3 py-1 rounded-lg text-sm">
                تکنولوژی
              </span>
            </div>
          </div>

          {/* Comments Section */}
          <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 md:p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                نظرات ({comments.length})
              </h2>
              <button 
                onClick={() => setShowCommentForm(true)}
                className="bg-yellow-400 text-gray-900 px-4 py-2 rounded-lg hover:bg-yellow-300 transition-colors"
              >
                نوشتن نظر
              </button>
            </div>

            {/* Comment Form */}
            {showCommentForm && (
              <div className="bg-gray-800 rounded-xl p-6 mb-6 border border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">
                    {replyingTo ? `پاسخ به ${replyingTo.name}` : 'ثبت نظر جدید'}
                  </h3>
                  {replyingTo && (
                    <button 
                      onClick={cancelReply}
                      className="text-gray-400 hover:text-white text-sm transition-colors"
                    >
                      انصراف از پاسخ
                    </button>
                  )}
                </div>
                <form onSubmit={handleCommentSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="name"
                      placeholder="نام شما *"
                      required
                      value={commentForm.name}
                      onChange={handleCommentChange}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="ایمیل شما *"
                      required
                      value={commentForm.email}
                      onChange={handleCommentChange}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <textarea
                    name="content"
                    placeholder={replyingTo ? "پاسخ خود را بنویسید..." : "نظر شما *"}
                    required
                    rows="4"
                    value={commentForm.content}
                    onChange={handleCommentChange}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                  />
                  <div className="flex items-center space-x-4 space-x-reverse">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="bg-yellow-400 text-gray-900 px-6 py-3 rounded-lg hover:bg-yellow-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 space-x-reverse"
                    >
                      {submitting && <Loader className="animate-spin h-4 w-4" />}
                      <Send size={16} />
                      <span>{replyingTo ? 'ارسال پاسخ' : 'ارسال نظر'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCommentForm(false);
                        cancelReply();
                      }}
                      className="bg-gray-700 text-gray-300 px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      انصراف
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Comments List */}
            <div className="space-y-6">
              {comments.map((comment) => (
                <CommentItem 
                  key={comment.id} 
                  comment={comment} 
                  getTimeAgo={getTimeAgo}
                  onReply={handleReply}
                  depth={0}
                />
              ))}
            </div>

            {comments.length === 0 && !showCommentForm && (
              <div className="text-center py-8">
                <MessageCircle size={48} className="text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">هنوز نظری ثبت نشده است. اولین نفری باشید که نظر می‌دهد!</p>
              </div>
            )}
          </div>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 md:p-8">
              <h2 className="text-2xl font-bold text-white mb-6">مقالات مرتبط</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedPosts.map((relatedPost) => (
                  <Link
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.slug}`}
                    className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700 hover:border-yellow-400 transition-all group"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={getFullImageUrl(relatedPost.featured_image)}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute top-2 left-2 bg-yellow-400 text-gray-900 px-2 py-1 rounded text-xs font-bold">
                        {relatedPost.category_name}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-white text-sm mb-2 line-clamp-2 group-hover:text-yellow-400 transition-colors">
                        {relatedPost.title}
                      </h3>
                      <p className="text-gray-400 text-xs mb-3 line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-gray-500 text-xs">
                        <span>{getTimeAgo(relatedPost.published_at)}</span>
                        <span className="flex items-center space-x-1 space-x-reverse">
                          <Eye size={12} />
                          <span>{relatedPost.view_count || 0}</span>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 space-x-reverse text-yellow-400 hover:text-yellow-300 transition-colors"
            >
              <ChevronRight size={20} />
              <span>بازگشت به بلاگ</span>
            </button>
            
            <Link
              to="/blog"
              className="bg-yellow-400 text-gray-900 px-6 py-2 rounded-lg hover:bg-yellow-300 transition-colors font-medium"
            >
              مشاهده همه مقالات
            </Link>
          </div>
        </div>

        {/* استایل‌های سفارشی برای محتوای بلاگ */}
        <style jsx>{`
          .blog-content {
            color: #d1d5db;
            line-height: 2;
            font-size: 1.125rem;
          }
          .blog-content h1, .blog-content h2, .blog-content h3, .blog-content h4 {
            color: white;
            margin-top: 2rem;
            margin-bottom: 1rem;
            font-weight: bold;
          }
          .blog-content h1 {
            font-size: 2rem;
            border-right: 4px solid #fbbf24;
            padding-right: 1rem;
          }
          .blog-content h2 {
            font-size: 1.5rem;
            border-right: 4px solid #fbbf24;
            padding-right: 1rem;
          }
          .blog-content h3 {
            font-size: 1.25rem;
          }
          .blog-content p {
            margin-bottom: 1.5rem;
            text-align: justify;
          }
          .blog-content ul, .blog-content ol {
            margin-right: 1.5rem;
            margin-bottom: 1.5rem;
          }
          .blog-content li {
            margin-bottom: 0.5rem;
            position: relative;
          }
          .blog-content ul li:before {
            content: "•";
            color: #fbbf24;
            font-weight: bold;
            display: inline-block;
            width: 1em;
            margin-right: 0.5em;
          }
          .blog-content blockquote {
            border-right: 4px solid #6b7280;
            padding-right: 1rem;
            margin: 2rem 0;
            font-style: italic;
            color: #9ca3af;
            background: #1f2937;
            padding: 1.5rem;
            border-radius: 0.5rem;
          }
          .blog-content img {
            max-width: 100%;
            height: auto;
            border-radius: 0.5rem;
            margin: 1.5rem 0;
          }
          .blog-content code {
            background: #374151;
            padding: 0.2rem 0.4rem;
            border-radius: 0.25rem;
            font-family: monospace;
          }
          .blog-content pre {
            background: #1f2937;
            padding: 1rem;
            border-radius: 0.5rem;
            overflow-x: auto;
            margin: 1.5rem 0;
          }
          .blog-content table {
            width: 100%;
            border-collapse: collapse;
            margin: 1.5rem 0;
          }
          .blog-content th, .blog-content td {
            border: 1px solid #374151;
            padding: 0.75rem;
            text-align: right;
          }
          .blog-content th {
            background: #374151;
            color: white;
          }
        `}</style>
      </div>
    </>
  );
};

// کامپوننت برای نمایش هر نظر
const CommentItem = ({ comment, getTimeAgo, onReply, depth = 0 }) => {
  const [showReplies, setShowReplies] = useState(true);
  const [showReplyForm, setShowReplyForm] = useState(false);

  const maxDepth = 3; // حداکثر عمق برای پاسخ‌ها

  return (
    <div className={`bg-gray-800 rounded-xl p-4 border border-gray-700 ${depth > 0 ? 'mr-4' : ''}`}>
      <div className="flex items-start space-x-3 space-x-reverse">
        <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-sm">
            {comment.author_initials || comment.name?.charAt(0) || 'U'}
          </span>
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h4 className="font-bold text-white">{comment.name}</h4>
              <span className="text-gray-400 text-sm">{getTimeAgo(comment.created_at)}</span>
            </div>
            {depth < maxDepth && (
              <button 
                onClick={() => onReply(comment)}
                className="text-gray-400 hover:text-yellow-400 text-sm transition-colors flex items-center space-x-1 space-x-reverse"
              >
                <MessageCircle size={14} />
                <span>پاسخ</span>
              </button>
            )}
          </div>
          <p className="text-gray-300 leading-relaxed">{comment.content}</p>

          {/* Replies */}
          {comment.replies && comment.replies.length > 0 && (
            <div className="mt-4">
              <button 
                onClick={() => setShowReplies(!showReplies)}
                className="flex items-center space-x-1 space-x-reverse text-yellow-400 hover:text-yellow-300 text-sm mb-3 transition-colors"
              >
                <span>{showReplies ? '▲' : '▼'}</span>
                <span>{showReplies ? 'پنهان کردن' : 'نمایش'} پاسخ‌ها ({comment.replies.length})</span>
              </button>
              
              {showReplies && (
                <div className="space-y-4 border-t border-gray-700 pt-4 mr-4">
                  {comment.replies.map((reply) => (
                    <CommentItem 
                      key={reply.id} 
                      comment={reply} 
                      getTimeAgo={getTimeAgo}
                      onReply={onReply}
                      depth={depth + 1}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogPost;